import { NUMERIC_PREFIX_POSE, SEMAPHORE_CODE, SEMAPHORE_NUMERALS } from "./code";
import type { FlagPose } from "./code";

/// A single step in a semaphore message animation. Discriminated
/// on `kind` so the renderer can show different captions
/// ("Numbers follow…", a digit count, the rest pose) without
/// reaching into the original text.

export type PoseFrame =
  /// A single alphabet letter pose.
  | { kind: "letter"; char: string; pose: FlagPose }
  /// A digit pose. By convention the renderer should always show
  /// a `numericPrefix` frame before any `digit` frames so the
  /// viewer knows they're looking at a number.
  | { kind: "digit"; char: string; pose: FlagPose }
  /// The "numbers follow" prefix pose (semaphore J). Tells the
  /// viewer that the next pose(s) carry digit meaning per the
  /// SEMAPHORE_NUMERALS table.
  | { kind: "numericPrefix"; pose: FlagPose }
  /// Both arms down — used for word breaks AND when transitioning
  /// out of number mode back into letters, so the viewer sees the
  /// number group close before the next letter starts.
  | { kind: "rest" };

/// Walk text and produce a pose sequence that handles letters,
/// digits (with proper numeric-prefix mechanic), and spaces.
/// Anything else (punctuation, accented chars) is silently
/// dropped — there's no semaphore convention to fall back on.
export function buildPoseSequence(text: string): PoseFrame[] {
  const out: PoseFrame[] = [];
  let inNumberMode = false;
  for (const ch of text.toUpperCase()) {
    if (ch === " ") {
      inNumberMode = false;
      out.push({ kind: "rest" });
      continue;
    }
    if (SEMAPHORE_NUMERALS[ch] !== undefined) {
      // Digits: emit prefix only when entering number mode;
      // consecutive digits skip the prefix.
      if (!inNumberMode) {
        out.push({ kind: "numericPrefix", pose: NUMERIC_PREFIX_POSE });
        inNumberMode = true;
      }
      const digitLetter = SEMAPHORE_NUMERALS[ch]!;
      out.push({
        kind: "digit",
        char: ch,
        pose: SEMAPHORE_CODE[digitLetter]!,
      });
      continue;
    }
    if (SEMAPHORE_CODE[ch] !== undefined) {
      // Letters: if we just emitted digits, close the number group
      // with a rest frame.
      if (inNumberMode) {
        out.push({ kind: "rest" });
        inNumberMode = false;
      }
      out.push({ kind: "letter", char: ch, pose: SEMAPHORE_CODE[ch]! });
    }
  }
  return out;
}
